import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { MapPin, Tag, ExternalLink, Filter, SortAsc, SortDesc, Search } from 'lucide-react';
import AdvancedSearchBar from '../components/AdvancedSearchBar';
import searchService, { SearchResult } from '../services/searchService';

export default function SearchResults() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState<'relevance' | 'name' | 'type'>('relevance');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [filterType, setFilterType] = useState<'all' | 'trader' | 'craftie' | 'hospitality' | 'professional'>('all');
  const [showFilters, setShowFilters] = useState(false);

  const query = searchParams.get('q') || '';
  const location = searchParams.get('location') || '';
  const type = searchParams.get('type') || '';

  // Initial search on page load
  useEffect(() => {
    if (query) {
      performSearch(query, location, type);
    }
  }, [query, location, type]);

  // Perform search
  const performSearch = async (searchQuery: string, searchLocation?: string, businessType?: string) => {
    if (!searchQuery.trim()) return;

    setLoading(true);
    try {
      const response = await searchService.searchBusinesses({
        query: searchQuery.trim(),
        location: searchLocation?.trim() || undefined,
        businessType: (businessType as any) || undefined,
        limit: 100,
        similarityThreshold: 0.3
      });

      const formattedResults = searchService.formatSearchResults(response.results);
      setResults(formattedResults);

      console.log('🔍 Search results loaded:', {
        query: searchQuery,
        results: formattedResults.length,
        stats: searchService.getSearchStats(formattedResults)
      });

    } catch (error) {
      console.error('❌ Search failed:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle new search from search bar
  const handleSearch = (newResults: SearchResult[]) => {
    setResults(newResults);
  };

  // Handle query change from search bar
  const handleQueryChange = (newQuery: string) => {
    if (newQuery !== query) {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        if (newQuery) {
          newParams.set('q', newQuery);
        } else {
          newParams.delete('q');
        }
        return newParams;
      });
    }
  };

  // Sort results
  const sortedResults = React.useMemo(() => {
    let filtered = results;

    // Apply type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(result => result.business_type === filterType);
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'relevance':
          comparison = b.relevance_score - a.relevance_score;
          break;
        case 'name':
          comparison = a.store_name.localeCompare(b.store_name);
          break;
        case 'type':
          comparison = a.business_type.localeCompare(b.business_type);
          break;
      }

      return sortOrder === 'desc' ? comparison : -comparison;
    });

    return sorted;
  }, [results, sortBy, sortOrder, filterType]);

  // Build canonical link per business type (slug-aware for traders)
  const getResultLink = (result: SearchResult) => {
    switch (result.business_type) {
      case 'trader':
        return `/trader/${result.slug ?? result.id}`;
      case 'craftie':
        return `/craftsman/${result.slug ?? result.id}`;
      case 'hospitality':
        return `/hospitality/${result.slug ?? result.id}`;
      case 'professional':
        return `/professional/${result.slug ?? result.id}`;
      default:
        return `/trader/${result.slug ?? result.id}`;
    }
  };

  // Get business type badge color
  const getBusinessTypeBadge = (type: string) => {
    const colors = {
      trader: 'bg-blue-100 text-blue-800',
      craftie: 'bg-green-100 text-green-800',
      hospitality: 'bg-purple-100 text-purple-800',
      professional: 'bg-orange-100 text-orange-800'
    };
    return colors[type as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  // Get match type badge
  const getMatchTypeBadge = (matchType: string) => {
    const badges = {
      exact_name: { label: 'Exact Match', color: 'bg-green-100 text-green-800' },
      exact_description: { label: 'Description Match', color: 'bg-blue-100 text-blue-800' },
      keyword_match: { label: 'Keyword Match', color: 'bg-yellow-100 text-yellow-800' },
      fulltext_match: { label: 'Text Match', color: 'bg-gray-100 text-gray-800' },
      fuzzy_match: { label: 'Similar Match', color: 'bg-orange-100 text-orange-800' }
    };
    return badges[matchType as keyof typeof badges] || badges.fulltext_match;
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      {/* Header with Search */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">

          <AdvancedSearchBar
            onSearch={handleSearch}
            onQueryChange={handleQueryChange}
            showFilters={true}
            showSuggestions={true}
            autoSearch={false}
          />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Filters */}
          <div className="lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Filters</h3>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="lg:hidden p-1 text-gray-500 hover:text-gray-700"
                >
                  <Filter className="h-4 w-4" />
                </button>
              </div>

              <div className={`space-y-6 ${showFilters ? 'block' : 'hidden lg:block'}`}>
                {/* Business Type Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Business Type
                  </label>
                  <div className="space-y-2">
                    {[
                      { value: 'all', label: 'All Types', count: results.length },
                      { value: 'trader', label: 'Traders', count: results.filter(r => r.business_type === 'trader').length },
                      { value: 'craftie', label: 'Crafties', count: results.filter(r => r.business_type === 'craftie').length },
                      { value: 'hospitality', label: 'Hospitality', count: results.filter(r => r.business_type === 'hospitality').length },
                      { value: 'professional', label: 'Professionals', count: results.filter(r => r.business_type === 'professional').length }
                    ].map(option => (
                      <label key={option.value} className="flex items-center">
                        <input
                          type="radio"
                          name="businessType"
                          value={option.value}
                          checked={filterType === option.value}
                          onChange={(e) => setFilterType(e.target.value as any)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          {option.label} ({option.count})
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Sort Options */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Sort By
                  </label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as any)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="relevance">Relevance</option>
                    <option value="name">Business Name</option>
                    <option value="type">Business Type</option>
                  </select>

                  <button
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    className="mt-2 flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800"
                  >
                    {sortOrder === 'asc' ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />}
                    {sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">Searching...</span>
              </div>
            ) : sortedResults.length > 0 ? (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    Showing {sortedResults.length} of {results.length} results
                  </p>
                </div>

                {sortedResults.map((result) => (
                  <div
                    key={result.id}
                    className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-6"
                  >
                    <div className="flex items-start gap-4">
                      {/* Avatar */}
                      <img
                        src={result.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${result.store_name}`}
                        alt={result.store_name}
                        className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                      />

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-1">
                              <Link
                                to={getResultLink(result)}
                                className="hover:text-blue-600 transition-colors"
                              >
                                {result.store_name}
                              </Link>
                            </h3>
                            <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                              <MapPin className="h-4 w-4" />
                              {result.location}
                            </div>
                          </div>

                          <div className="flex flex-col items-end gap-2">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getBusinessTypeBadge(result.business_type)}`}>
                              {result.business_type}
                            </span>
                            {result.match_type && (
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getMatchTypeBadge(result.match_type).color}`}>
                                {getMatchTypeBadge(result.match_type).label}
                              </span>
                            )}
                          </div>
                        </div>

                        <p className="text-gray-700 text-sm mb-3 line-clamp-2">
                          {result.description}
                        </p>

                        {/* Keywords */}
                        {result.keywords && result.keywords.length > 0 && (
                          <div className="flex items-center gap-2 mb-3">
                            <Tag className="h-4 w-4 text-gray-400" />
                            <div className="flex flex-wrap gap-1">
                              {result.keywords.slice(0, 5).map((keyword, idx) => (
                                <span
                                  key={idx}
                                  className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full"
                                >
                                  {keyword}
                                </span>
                              ))}
                              {result.keywords.length > 5 && (
                                <span className="px-2 py-1 text-xs text-gray-500">
                                  +{result.keywords.length - 5} more
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex items-center justify-between">
                          <div />

                          <Link
                            to={getResultLink(result)}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            View Details
                            <ExternalLink className="h-4 w-4" />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : query ? (
              <div className="text-center py-12">
                <div className="mb-4">
                  <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No results found for "{query}"
                  </h3>
                  <p className="text-gray-600">
                    Try adjusting your search terms or filters
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6 max-w-md mx-auto">
                  <h4 className="font-medium text-gray-900 mb-2">Search Tips:</h4>
                  <ul className="text-sm text-gray-600 text-left space-y-1">
                    <li>• Try different keywords</li>
                    <li>• Check spelling</li>
                    <li>• Use more general terms</li>
                    <li>• Remove location filters</li>
                  </ul>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Start searching for businesses
                </h3>
                <p className="text-gray-600">
                  Enter a search term above to find local businesses and services
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
